import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  take,
  takeUntil,
  timeout,
} from 'rxjs/operators';
import { Router, ActivatedRoute } from '@angular/router';
import * as ReferentielSelectors from '../../../store/referentiel/referentiel.selectors';
import * as ReferentielActions from '../../../store/referentiel/referentiel.actions';
import { environment } from '../../../../environments';
import {
  Country,
  Insurer,
  Language,
  ServiceAvailable,
} from '../../../core/interfaces/doctor.interface';
import { ReferentielService } from '../../../core/services/referentiel.service';
import { UrlService } from '../../../core/services/url.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';

interface Result {
  id: string;
  result: string;
  name?: string;
  type: string;
}

@Component({
  selector: 'app-search-and-filtrer',
  templateUrl:
    environment.theme.default === 'dpp'
      ? './search-and-filtrer.dpp.component.html'
      : './search-and-filtrer.component.html',
  styleUrl: './search-and-filtrer.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
  ],
})
export class SearchAndFiltrerComponent implements OnInit, OnDestroy {
  currentTheme = environment.theme.default;

  private readonly destroy$ = new Subject<void>();
  languages$: Observable<Language[] | null>;
  services$: Observable<ServiceAvailable[] | null>;
  insurers$: Observable<Insurer[] | null>;
  countries$: Observable<Country[] | null>;
  loading$: Observable<boolean>;
  error$: Observable<string | null>;
  selectedCountry: Country | null = null;

  searchForm: FormGroup;
  towns: Result[] = [];
  results: Result[] = [];
  townLoading = false;
  queryLoading = false;

  selectedLanguage: string = '';
  selectedService: string = '';
  selectedInsurer: string = '';

  // Propriété pour contrôler l'affichage du champ service
  showServiceField: boolean = false;

  // Propriété pour contrôler l'affichage du champ langue
  showLanguageField: boolean = true;

  private fetchedTownIds = new Set<string>();
  private fetchedSpecialisationIds = new Set<string>();

  constructor(
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private referentielService: ReferentielService,
    private urlService: UrlService,
  ) {
    this.languages$ = this.store.select(ReferentielSelectors.selectLanguages);
    this.services$ = this.store.select(ReferentielSelectors.selectServices);
    this.insurers$ = this.store.select(ReferentielSelectors.selectInsurers);
    this.countries$ = this.store.select(ReferentielSelectors.selectCountries);
    this.loading$ = this.store.select(
      ReferentielSelectors.selectReferentielLoading,
    );
    this.error$ = this.store.select(
      ReferentielSelectors.selectReferentielError,
    );

    this.searchForm = this.fb.group({
      town: [''],
      query: [''],
    });
  }

  ngOnInit(): void {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('defaultCountry');
      if (saved) {
        try {
          this.selectedCountry = JSON.parse(saved);
        } catch {
          this.selectedCountry = null;
        }
      }
    }
    this.onSearchTown();
    this.onSearchQuery();

    this.route.parent?.params
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        const countryCode = params['country']?.toUpperCase() || '';
        this.insurers$ = this.referentielService.getInsurers(countryCode);
      });

    this.loadReferentielData();
    this.loadUrlParams();

    // Vérifier si la route actuelle commence par '/medecins'
    this.checkCurrentRoute();
  }

  // Méthode pour vérifier la route actuelle
  private checkCurrentRoute(): void {
    // Vérifier la route actuelle immédiatement
    this.showServiceField = this.router.url.includes('/medecins');
    this.showLanguageField = !this.router.url.includes('/pharmacies');

    // S'abonner aux changements de route
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.showServiceField = this.router.url.includes('/medecins');
      this.showLanguageField = !this.router.url.includes('/pharmacies');
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Méthode pour déterminer les classes de grille selon les champs visibles
  getGridClasses(): string {
    const visibleFields = [
      this.showLanguageField,
      this.showServiceField,
      true,
    ].filter(Boolean).length;
    return `grid-cols-1 lg:grid-cols-${visibleFields}`;
  }

  onSearchTown() {
    const townControl = this.searchForm.get('town');
    if (townControl) {
      townControl.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          switchMap((term) => {
            if (term == null || typeof term !== 'string') {
              return of([]);
            }
            this.townLoading = true;
            return this.referentielService.townSearchAutocomplete(term).pipe(
              timeout(120000),
              catchError(() => of([])),
            );
          }),
          takeUntil(this.destroy$),
        )
        .subscribe((res) => {
          this.townLoading = false;
          this.towns = Array.isArray(res) ? res : [];
        });
    }
  }

  onSearchQuery() {
    const queryControl = this.searchForm.get('query');
    if (queryControl) {
      queryControl.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          switchMap((term) => {
            if (
              term == null ||
              typeof term !== 'string' ||
              !(term as string).trim()
            ) {
              return of([]);
            }
            this.queryLoading = true;
            return this.referentielService
              .searchDoctorsWithSpecialities((term as string).trim())
              .pipe(
                timeout(120000),
                catchError(() => of([])),
              );
          }),
          takeUntil(this.destroy$),
        )
        .subscribe((res) => {
          this.queryLoading = false;
          this.results = Array.isArray(res) ? res : [];
        });
    }
  }

  /** Indique si le champ ville a une valeur (objet ou chaîne non vide). */
  get hasTownValue(): boolean {
    const v = this.searchForm.get('town')?.value;
    if (v == null) return false;
    if (typeof v === 'string') return (v as string).trim() !== '';
    return true;
  }

  /** Indique si le champ prestataire a une valeur (objet ou chaîne non vide). */
  get hasQueryValue(): boolean {
    const v = this.searchForm.get('query')?.value;
    if (v == null) return false;
    if (typeof v === 'string') return (v as string).trim() !== '';
    return true;
  }

  clearTown(): void {
    this.searchForm.patchValue({ town: '' }, { emitEvent: false });
    this.towns = [];
  }

  clearQuery(): void {
    this.searchForm.patchValue({ query: '' }, { emitEvent: false });
    this.results = [];
  }

  displayTownFn(item: Result | null): string {
    return item?.result || '';
  }

  displayQueryFn(item: Result | string | null): string {
    if (item == null) return '';
    if (typeof item === 'string') return item;
    return item.type === 'speciality' ? item.name || '' : item.result || '';
  }

  private loadReferentielData(): void {
    this.store
      .select(ReferentielSelectors.selectLanguages)
      .pipe(take(1))
      .subscribe((languages) => {
        if (!languages || languages.length === 0) {
          this.store.dispatch(ReferentielActions.loadLanguages());
        }
      });

    this.store
      .select(ReferentielSelectors.selectServices)
      .pipe(take(1))
      .subscribe((services) => {
        if (!services || services.length === 0) {
          this.store.dispatch(ReferentielActions.loadServices());
        }
      });

    this.store
      .select(ReferentielSelectors.selectCountries)
      .pipe(take(1))
      .subscribe((countries) => {
        if (!countries || countries.length === 0) {
          this.store.dispatch(ReferentielActions.loadCountries());
        }
      });
  }

  private loadUrlParams(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (params) => {
        // Ne charger la langue que si le champ est visible
        this.selectedLanguage = this.showLanguageField
          ? params['language'] || ''
          : '';
        // Ne charger le service que si le champ est visible (route /medecins)
        this.selectedService = this.showServiceField
          ? params['service'] || ''
          : '';
        this.selectedInsurer = params['insurer'] || '';

        const patch: any = {};

        // Town
        if (params['townId']) {
          let foundTown = this.towns.find((t) => t.id == params['townId']);
          if (!foundTown) {
            // Fetch from API if not in list
            foundTown = await this.referentielService
              .getTownById(params['townId'])
              .toPromise();
            // Map to autocomplete format if needed
            if (foundTown) {
              foundTown = {
                ...foundTown,
                result: foundTown.name ?? '',
                type: 'town',
              };
              this.towns = [...this.towns, foundTown];
            }
          }
          if (foundTown) patch.town = foundTown;
        }

        // Query: spécialité (objet) ou recherche texte libre (string)
        if (params['specialisationIds']) {
          let foundQuery = this.results.find(
            (q) =>
              q.id == params['specialisationIds'] && q.type === 'speciality',
          );
          if (!foundQuery) {
            foundQuery = await this.referentielService
              .getDoctorSpecializationById(params['specialisationIds'])
              .toPromise();
            if (foundQuery) {
              foundQuery = {
                ...foundQuery,
                result: foundQuery.name ?? '',
                type: 'speciality',
              };
              this.results = [...this.results, foundQuery];
            }
          }
          if (foundQuery) patch.query = foundQuery;
        } else if (params['query'] && typeof params['query'] === 'string') {
          const queryStr = (params['query'] as string).trim();
          if (queryStr) patch.query = queryStr;
        }

        if (Object.keys(patch).length) {
          this.searchForm.patchValue(patch, { emitEvent: false });
        }
      });
  }

  onLanguageChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedLanguage = target.value;

    if (!this.selectedLanguage) {
      // Remove language parameter if empty
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          language: null,
        },
        queryParamsHandling: 'merge',
      });
    } else {
      this.updateUrlParams();
    }
  }

  onServiceChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedService = target.value;

    if (!this.selectedService) {
      // Remove service parameter if empty
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          service: null,
        },
        queryParamsHandling: 'merge',
      });
    } else {
      this.updateUrlParams();
    }
  }

  onInsurerChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedInsurer = target.value;

    if (!this.selectedInsurer) {
      // Remove insurer parameter if empty
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          insurer: null,
        },
        queryParamsHandling: 'merge',
      });
    } else {
      this.updateUrlParams();
    }
  }

  onSearch(): void {
    const formValues = this.searchForm.value;
    const queryParams: any = {};

    // Ne traiter la langue que si elle est visible
    if (this.showLanguageField) {
      if (this.selectedLanguage) queryParams.language = this.selectedLanguage;
      else queryParams.language = null;
    }

    // Ne traiter le service que s'il est visible
    if (this.showServiceField) {
      if (this.selectedService) queryParams.service = this.selectedService;
      else queryParams.service = null;
    }

    if (this.selectedInsurer) queryParams.insurer = this.selectedInsurer;
    else queryParams.insurer = null;

    // Town
    if (
      formValues.town &&
      typeof formValues.town === 'object' &&
      formValues.town.id
    ) {
      queryParams.townId = formValues.town.id;
    } else {
      queryParams.townId = null;
    }

    // Query : spécialité sélectionnée (ID) ou recherche texte libre (tolérante)
    if (
      formValues.query &&
      typeof formValues.query === 'object' &&
      formValues.query.type === 'speciality'
    ) {
      queryParams.specialisationIds = formValues.query.id;
      queryParams.query = null;
    } else if (
      typeof formValues.query === 'string' &&
      formValues.query.trim().length > 0
    ) {
      queryParams.query = formValues.query.trim();
      queryParams.specialisationIds = null;
    } else {
      queryParams.query = null;
      queryParams.specialisationIds = null;
    }

    // Navigate and merge params, removing cleared ones
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  // Autocomplete selection handler for doctor/structure/pharmacy
  onQuerySelected(event: any): void {
    const selectedQuery = event.option.value as Result;
    if (!selectedQuery || !selectedQuery.type) return;

    switch (selectedQuery.type) {
      case 'doctor':
        this.router.navigate([
          `/${this.selectedCountry?.code?.toLowerCase()}/medecins/${selectedQuery.id}`,
        ]);
        break;
      case 'structure':
        this.router.navigate([
          `/${this.selectedCountry?.code?.toLowerCase()}/structures/${selectedQuery.id}`,
        ]);
        break;
      case 'pharmacy':
        this.router.navigate([
          `/${this.selectedCountry?.code?.toLowerCase()}/pharmacies/${selectedQuery.id}`,
        ]);
        break;
      // For speciality, do nothing (handled by search button)
      default:
        break;
    }
  }

  private updateUrlParams(withoutOptions: boolean = false): void {
    const formValues = this.searchForm.value;
    const queryParams: any = {};

    if (this.selectedLanguage && !withoutOptions) {
      queryParams.language = this.selectedLanguage;
    }

    if (this.selectedService && !withoutOptions) {
      queryParams.service = this.selectedService;
    }

    if (this.selectedInsurer && !withoutOptions) {
      queryParams.insurer = this.selectedInsurer;
    }

    if (
      formValues.town &&
      typeof formValues.town === 'object' &&
      formValues.town.id
    ) {
      queryParams.townId = formValues.town.id;
    } else {
      queryParams.townId = null;
    }

    if (
      formValues.query &&
      typeof formValues.query === 'object' &&
      formValues.query.type === 'speciality'
    ) {
      queryParams.specialisationIds = formValues.query.id;
      queryParams.query = null;
    } else if (
      typeof formValues.query === 'string' &&
      formValues.query.trim().length > 0
    ) {
      queryParams.query = formValues.query.trim();
      queryParams.specialisationIds = null;
    } else {
      queryParams.query = null;
      queryParams.specialisationIds = null;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: withoutOptions ? 'replace' : 'merge',
    });
  }
}
